import { useState, useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { useModuleStatus } from '@/hooks/useModuleStatus.ts'
import {
  TrendingUp, DollarSign, Users,
  Building2, Ellipsis, AlertTriangle, Clock, ExternalLink,
  GripVertical, MapPin, User, UserPlus,
} from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  rectIntersection,
  useDraggable,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  Badge,
  BetaBadge,
  Button,
  Card,
  DataTable,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Tabs,
  TabsList,
  TabsTrigger,
  dateColumn,
  textColumn,
  useToast,
  type BadgeProps,
  type ColumnDef,
} from '@/components/ui'
import { HandoffDialog, type HandoffProspect } from './HandoffDialog.tsx'

interface Deal {
  id: string
  tanssTicketId: string
  title: string
  customerName: string | null
  companyId: number | null
  assignedTo: string | null
  assignedToEmployeeId: number | null
  phase: string
  probability: number
  value: number
  dueDate: string | null
  deadline: string | null
  status: unknown
  tags: string[]
  tagIds: number[]
  internalNote: string | null
  sapQuotationDocEntry: number | null
  sapOrderDocEntry: number | null
  localDealId: string | null
  createdAt: string | null
  updatedAt: string | null
}

interface PhaseTagMapping {
  tag: string
  phase: string
  probability: number
  tagId: number | null
}

interface Prospect {
  id: string
  companyName: string
  city: string | null
  icpScore: number
  status: string
  contactFirstName: string | null
  contactLastName: string | null
  contactRole: string | null
  contactEmail: string | null
  contactPhone: string | null
  statusChangedAt: string | null
}

type BoardCard =
  | { kind: 'prospect'; id: string; prospect: Prospect }
  | { kind: 'deal'; id: string; deal: Deal }

type BoardSource = 'prospect' | 'deal' | 'both'

type PhaseVariant = NonNullable<BadgeProps['variant']>

interface BoardPhase {
  id: string
  name: string
  hint: string
  /** Top border color of the Kanban column (visual accent, not badge). */
  accentBorder: string
  /** Badge variant for the phase pill in card/list view. */
  badgeVariant: PhaseVariant
  source: BoardSource
  /** Maps to data source: prospect.status (when source is prospect/both) */
  prospectStatus?: string
  /** Maps to data source: deal.phase (when source is deal/both) */
  dealPhase?: string
  /** For handoff trigger: initial phase when dropping from prospect */
  handoffInitialTag?: 'Lead neu' | 'Angebot benötigt'
}

const PHASES: BoardPhase[] = [
  { id: 'recherche',      name: 'Recherche',      hint: 'Erfasst, noch nicht qualifiziert',  accentBorder: 'border-t-gray-400',    badgeVariant: 'neutral', source: 'prospect', prospectStatus: 'new' },
  { id: 'qualifiziert',   name: 'Qualifiziert',   hint: 'Passt zum ICP – wartet auf Brief',  accentBorder: 'border-t-blue-400',    badgeVariant: 'info',    source: 'both',     prospectStatus: 'qualified', dealPhase: 'qualified' },
  { id: 'erstansprache',  name: 'Erstansprache',  hint: 'Brief raus, Nachfass an Tag 5',     accentBorder: 'border-t-amber-400',   badgeVariant: 'warning', source: 'prospect', prospectStatus: 'letter_sent' },
  { id: 'follow-up',      name: 'Follow-up',      hint: '2. Versuch läuft',                  accentBorder: 'border-t-purple-400',  badgeVariant: 'accent',  source: 'prospect', prospectStatus: 'follow_up' },
  { id: 'im-dialog',      name: 'Im Dialog',      hint: 'Reaktion erhalten',                 accentBorder: 'border-t-green-400',   badgeVariant: 'success', source: 'prospect', prospectStatus: 'interested' },
  { id: 'termin',         name: 'Termin',         hint: 'Termin fest – bereit für Vertrieb', accentBorder: 'border-t-emerald-500', badgeVariant: 'success', source: 'prospect', prospectStatus: 'meeting_scheduled' },
  { id: 'lead',           name: 'Lead',           hint: 'TANSS-Ticket, Vertrieb übernimmt',  accentBorder: 'border-t-sky-400',     badgeVariant: 'info',    source: 'deal',     dealPhase: 'lead',        handoffInitialTag: 'Lead neu' },
  { id: 'angebot',        name: 'Angebot',        hint: 'Angebot benötigt / versendet',      accentBorder: 'border-t-amber-500',   badgeVariant: 'warning', source: 'deal',     dealPhase: 'proposal',    handoffInitialTag: 'Angebot benötigt' },
  { id: 'verhandlung',    name: 'Verhandlung',    hint: 'In Verhandlung',                    accentBorder: 'border-t-purple-500',  badgeVariant: 'brand',   source: 'deal',     dealPhase: 'negotiation' },
  { id: 'gewonnen',       name: 'Gewonnen',       hint: 'Deal gewonnen',                     accentBorder: 'border-t-green-600',   badgeVariant: 'success', source: 'deal',     dealPhase: 'won' },
]

interface PipelinePhase extends BoardPhase {
  cards: BoardCard[]
  totalValue: number
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function KpiCard({
  icon,
  label,
  value,
  valueClassName,
}: {
  icon: React.ReactNode
  label: string
  value: string
  valueClassName?: string
}) {
  return (
    <Card>
      <Card.Body className="p-4">
        <p className="text-caption text-gray-500 flex items-center gap-1">
          {icon}
          {label}
        </p>
        <p className={cn('text-h3 font-bold text-gray-900 mt-1', valueClassName)}>{value}</p>
      </Card.Body>
    </Card>
  )
}

function getScoreVariant(score: number): PhaseVariant {
  if (score >= 80) return 'success'
  if (score >= 60) return 'info'
  if (score >= 40) return 'warning'
  return 'neutral'
}

function phaseForDeal(dealPhase: string): BoardPhase | undefined {
  return PHASES.find((p) => p.dealPhase === dealPhase)
}

function phaseForProspect(status: string): BoardPhase | undefined {
  return PHASES.find((p) => p.prospectStatus === status)
}

const PROSPECT_ACTIVE_STATUSES = new Set([
  'new', 'qualified', 'letter_sent', 'follow_up', 'interested', 'meeting_scheduled',
])

// ─── Droppable Column ───────────────────────────────────────────────────────

function DroppableColumn({ phase, children }: { phase: PipelinePhase; children: React.ReactNode }) {
  const a = useAnonymize()
  const { setNodeRef, isOver } = useDroppable({ id: phase.id })

  return (
    <div ref={setNodeRef} className="flex-shrink-0 w-[220px] flex flex-col min-h-0">
      <Card
        className={cn(
          'border-t-4 flex flex-col min-h-0 flex-1 transition-colors',
          phase.accentBorder,
          isOver && 'ring-2 ring-brand-400 bg-brand-50/30',
        )}
      >
        <div className="px-3 py-2 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-caption font-semibold text-gray-900">{phase.name}</h3>
            <Badge variant="neutral" size="sm">{phase.cards.length}</Badge>
          </div>
          <p className="text-caption text-gray-400 mt-0.5 truncate" title={phase.hint}>{phase.hint}</p>
          {phase.totalValue > 0 && (
            <p className="text-caption text-gray-500 font-medium mt-0.5">{a.fmtCurrency(phase.totalValue)}</p>
          )}
        </div>
        <div className="p-2 space-y-2 flex-1 overflow-y-auto min-h-[80px]">
          {children}
          {phase.cards.length === 0 && (
            <p className="text-caption text-gray-300 text-center py-8">
              {phase.source === 'deal' ? 'Keine Deals' : 'Keine Zielkunden'}
            </p>
          )}
        </div>
      </Card>
    </div>
  )
}

// ─── Prospect Card ──────────────────────────────────────────────────────────

function ProspectCard({
  prospect,
  onClick,
  dragListeners,
  isOverlay,
}: {
  prospect: Prospect
  onClick: () => void
  dragListeners?: Record<string, unknown>
  isOverlay?: boolean
}) {
  const a = useAnonymize()
  const scoreVariant = getScoreVariant(prospect.icpScore)
  const daysInPhase = prospect.statusChangedAt
    ? Math.max(0, Math.floor((Date.now() - new Date(prospect.statusChangedAt).getTime()) / (24 * 60 * 60 * 1000)))
    : null
  const contactName = [prospect.contactFirstName, prospect.contactLastName].filter(Boolean).join(' ')

  return (
    <div
      onClick={onClick}
      className={cn(
        'bg-white border rounded-md p-2.5 hover:shadow-card transition-shadow cursor-pointer group border-gray-200',
        isOverlay && 'shadow-popover rotate-2',
      )}
    >
      <div className="flex items-start justify-between gap-1">
        <p className="text-small font-medium text-gray-900 truncate flex-1">{a.name(prospect.companyName)}</p>
        {dragListeners && (
          <IconButton
            {...(dragListeners as Record<string, unknown>)}
            variant="ghost"
            size="xs"
            icon={<GripVertical />}
            aria-label="Karte ziehen"
            className="opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>
      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
        <Badge variant={scoreVariant} size="sm">{prospect.icpScore}</Badge>
        {prospect.city && (
          <span className="text-caption text-gray-500 inline-flex items-center gap-0.5">
            <MapPin className="w-2.5 h-2.5" />
            {prospect.city}
          </span>
        )}
      </div>
      {contactName && (
        <p className="text-caption text-gray-600 mt-1 truncate flex items-center gap-1">
          <User className="w-2.5 h-2.5 shrink-0 text-gray-400" />
          {contactName}
        </p>
      )}
      {daysInPhase !== null && (
        <p className="text-caption text-gray-400 mt-1">
          Seit {daysInPhase} {daysInPhase === 1 ? 'Tag' : 'Tagen'} in dieser Phase
        </p>
      )}
    </div>
  )
}

function DraggableProspectCard({ prospect, onClick }: { prospect: Prospect; onClick: () => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `prospect:${prospect.id}`,
    data: { kind: 'prospect', prospect },
  })
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.4 : 1,
  }
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <ProspectCard prospect={prospect} onClick={onClick} dragListeners={listeners} />
    </div>
  )
}

// ─── Draggable Deal Card ────────────────────────────────────────────────────

function DraggableDealCard({ deal, onClick }: { deal: Deal; onClick: () => void }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({ id: `deal:${deal.id}`, data: { kind: 'deal', deal } })

  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.4 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <DealCard deal={deal} onClick={onClick} dragListeners={listeners} />
    </div>
  )
}

function DealCard({
  deal,
  onClick,
  dragListeners,
  isOverlay,
}: {
  deal: Deal
  onClick: () => void
  dragListeners?: Record<string, any>
  isOverlay?: boolean
}) {
  const a = useAnonymize()
  const now = new Date()
  const overdue = deal.dueDate && new Date(deal.dueDate) < now
  const dueSoon = deal.dueDate && !overdue && (new Date(deal.dueDate).getTime() - now.getTime()) < 7 * 24 * 60 * 60 * 1000

  return (
    <div
      onClick={onClick}
      className={cn(
        'bg-white border rounded-md p-3 hover:shadow-card transition-shadow cursor-pointer group',
        overdue ? 'border-red-300 bg-red-50' : 'border-gray-200',
        isOverlay && 'shadow-popover rotate-2',
      )}
    >
      <div className="flex items-start justify-between gap-1">
        <p className="text-small font-medium text-gray-900 truncate flex-1">{deal.title}</p>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {dragListeners && (
            <IconButton
              {...dragListeners}
              variant="ghost"
              size="xs"
              icon={<GripVertical />}
              aria-label="Karte ziehen"
              className="opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <IconButton
            variant="ghost"
            size="xs"
            icon={<Ellipsis />}
            aria-label="Aktionen"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
      {deal.customerName && (
        <p className="text-caption text-gray-500 flex items-center gap-1 mt-1">
          <Building2 className="w-3 h-3" />{a.name(deal.customerName)}
        </p>
      )}
      <div className="flex items-center justify-between mt-2">
        {deal.value > 0 ? (
          <p className="text-small font-bold text-gray-900">{a.fmtCurrency(deal.value)}</p>
        ) : (
          <p className="text-caption text-gray-400 italic">Kein Wert</p>
        )}
        <span className="text-caption text-gray-400">#{a.docNum(deal.tanssTicketId)}</span>
      </div>
      {deal.dueDate && (
        <p className={cn(
          'text-caption mt-1 flex items-center gap-1',
          overdue ? 'text-red-600 font-medium' : dueSoon ? 'text-orange-500' : 'text-gray-400',
        )}>
          {overdue ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
          {overdue ? 'Überfällig: ' : 'Fällig: '}{new Date(deal.dueDate).toLocaleDateString('de-DE')}
        </p>
      )}
      {(deal.sapQuotationDocEntry || deal.sapOrderDocEntry) && (
        <div className="flex gap-1 mt-1">
          {deal.sapQuotationDocEntry && (
            <Badge variant="warning" size="sm">SAP-Angebot</Badge>
          )}
          {deal.sapOrderDocEntry && (
            <Badge variant="success" size="sm">SAP-Auftrag</Badge>
          )}
        </div>
      )}
      {deal.assignedTo && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <span className="text-caption text-gray-500">{deal.assignedTo}</span>
        </div>
      )}
    </div>
  )
}

// ─── Ticket Detail Modal ────────────────────────────────────────────────────

function TicketDetailModal({ deal, onClose }: { deal: Deal; onClose: () => void }) {
  const a = useAnonymize()
  const { data: ticket, isLoading } = useQuery<any>({
    queryKey: ['ticket-detail', deal.tanssTicketId],
    queryFn: () => apiClient.get(`/tickets/${deal.tanssTicketId}`).then((r) => r.data),
  })

  const phase = phaseForDeal(deal.phase)

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      size="xl"
      className="max-w-3xl"
      title={<span className="truncate block">{deal.title}</span>}
      description={`TANSS-Ticket #${a.docNum(deal.tanssTicketId)}`}
      footer={
        <>
          <Button
            variant="link"
            size="sm"
            leadingIcon={<ExternalLink />}
            onClick={() => window.open(`/tickets/${deal.tanssTicketId}`, '_blank', 'noopener,noreferrer')}
          >
            Vollständige Ticket-Ansicht öffnen
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Schließen
          </Button>
        </>
      }
    >
      {isLoading ? (
        <LoadingState />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-small">
            <div className="flex justify-between">
              <span className="text-gray-500">Kunde</span>
              <span className="text-gray-900 font-medium">{a.name(ticket?.customer?.name ?? deal.customerName ?? '') || '-'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-500">Phase</span>
              {phase ? (
                <Badge variant={phase.badgeVariant} size="sm">{phase.name}</Badge>
              ) : (
                <span className="text-gray-900">{deal.phase}</span>
              )}
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Verantwortlich</span>
              <span className="text-gray-900">{ticket?.assignee?.name ?? deal.assignedTo ?? '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Status</span>
              <span className="text-gray-900">{ticket?.status ?? '-'}</span>
            </div>
            {(deal.dueDate || ticket?.dueDate) && (
              <div className="flex justify-between">
                <span className="text-gray-500">Fällig</span>
                <span className={cn(
                  deal.dueDate && new Date(deal.dueDate) < new Date() ? 'text-red-600 font-medium' : 'text-gray-900',
                )}>
                  {new Date(deal.dueDate ?? ticket?.dueDate).toLocaleDateString('de-DE')}
                  {deal.dueDate && new Date(deal.dueDate) < new Date() && ' (überfällig)'}
                </span>
              </div>
            )}
            {ticket?.priority && (
              <div className="flex justify-between">
                <span className="text-gray-500">Priorität</span>
                <span className="text-gray-900">{ticket.priority}</span>
              </div>
            )}
            {ticket?.ecpPerson?.name && (
              <div className="flex justify-between">
                <span className="text-gray-500">Ansprechpartner</span>
                <span className="text-gray-900">{ticket.ecpPerson.name}</span>
              </div>
            )}
            {ticket?.ticketType && (
              <div className="flex justify-between">
                <span className="text-gray-500">Typ</span>
                <span className="text-gray-900">{ticket.ticketType}</span>
              </div>
            )}
          </div>

          {ticket?.content && (
            <div>
              <h4 className="text-caption font-semibold text-gray-500 uppercase tracking-wider mb-2">Beschreibung</h4>
              <div className="text-small text-gray-700 bg-gray-50 rounded-md p-3 whitespace-pre-wrap max-h-48 overflow-y-auto">
                {ticket.content}
              </div>
            </div>
          )}

          {(ticket?.internalContent || deal.internalNote) && (
            <div>
              <h4 className="text-caption font-semibold text-gray-500 uppercase tracking-wider mb-2">Interne Bemerkung</h4>
              <div className="text-small text-gray-700 bg-amber-50 border border-amber-100 rounded-md p-3 whitespace-pre-wrap max-h-32 overflow-y-auto">
                {ticket?.internalContent ?? deal.internalNote}
              </div>
            </div>
          )}

          {ticket?.tags && ticket.tags.length > 0 && (
            <div>
              <h4 className="text-caption font-semibold text-gray-500 uppercase tracking-wider mb-2">Tags</h4>
              <div className="flex flex-wrap gap-1.5">
                {(Array.isArray(ticket.tags) ? ticket.tags : []).map((tag: any, i: number) => {
                  const tagName = tag?.name ?? tag?.tagName ?? tag
                  return (
                    <Badge key={i} variant="neutral" size="sm">{String(tagName)}</Badge>
                  )
                })}
              </div>
            </div>
          )}

          {(deal.sapQuotationDocEntry || deal.sapOrderDocEntry) && (
            <div>
              <h4 className="text-caption font-semibold text-gray-500 uppercase tracking-wider mb-2">SAP-Verknüpfungen</h4>
              <div className="flex gap-2">
                {deal.sapQuotationDocEntry && (
                  <Badge variant="warning">SAP-Angebot #{a.docNum(deal.sapQuotationDocEntry)}</Badge>
                )}
                {deal.sapOrderDocEntry && (
                  <Badge variant="success">SAP-Auftrag #{a.docNum(deal.sapOrderDocEntry)}</Badge>
                )}
              </div>
            </div>
          )}

          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center gap-6 text-small">
              <div>
                <span className="text-gray-500">Geschätzter Wert:</span>{' '}
                <span className="font-bold text-gray-900">{deal.value > 0 ? a.fmtCurrency(deal.value) : '-'}</span>
              </div>
              <div>
                <span className="text-gray-500">Wahrscheinlichkeit:</span>{' '}
                <span className="font-bold text-gray-900">{deal.probability}%</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

// ─── Deal Metadata Dialog ───────────────────────────────────────────────────

function DealMetadataDialog({
  deal,
  onSave,
  onClose,
  saving,
}: {
  deal: Deal
  onSave: (data: Record<string, any>) => void
  onClose: () => void
  saving: boolean
}) {
  const [value, setValue] = useState(deal.value > 0 ? String(deal.value) : '')
  const [probability, setProbability] = useState(String(deal.probability))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave({
      value: Number(value) || 0,
      probability: Number(probability) || deal.probability || 10,
    })
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      size="md"
      title={deal.title}
      description="Deal-Daten bearbeiten"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" form="deal-metadata-form" loading={saving}>
            {saving ? 'Speichert...' : 'Speichern'}
          </Button>
        </>
      }
    >
      <form id="deal-metadata-form" onSubmit={handleSubmit} className="space-y-3">
        <Input
          label="Geschätzter Wert (EUR)"
          type="number"
          min={0}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="z.B. 25000"
        />
        <Input
          label="Gewinnwahrscheinlichkeit (%)"
          type="number"
          min={0}
          max={100}
          value={probability}
          onChange={(e) => setProbability(e.target.value)}
        />
      </form>
    </Modal>
  )
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function VertriebsboardPage() {
  const a = useAnonymize()
  const { isBeta } = useModuleStatus('vertriebsboard')
  const [view, setView] = useState<'board' | 'list'>('board')
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null)
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null)
  const [activeDrag, setActiveDrag] = useState<BoardCard | null>(null)
  const [handoffProspect, setHandoffProspect] = useState<{ prospect: HandoffProspect; initialPhase: 'Lead neu' | 'Angebot benötigt' } | null>(null)
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const showDropError = useCallback(
    (description: string) =>
      toast({ title: 'Verschieben nicht möglich', description, tone: 'warning' }),
    [toast],
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  )

  const { data: deals = [], isLoading: dealsLoading, error: dealsError } = useQuery<Deal[]>({
    queryKey: ['deals'],
    queryFn: () => apiClient.get('/deals').then((r) => r.data),
    retry: false,
    refetchInterval: 120_000,
  })

  const { data: prospectsResp, isLoading: prospectsLoading } = useQuery<{ prospects: Prospect[]; total: number }>({
    queryKey: ['vertrieb', 'prospects'],
    queryFn: () => apiClient.get('/acquisition/prospects?limit=500').then((r) => r.data),
    retry: false,
    staleTime: 60_000,
  })
  const prospects = prospectsResp?.prospects ?? []

  const { data: phaseMeta } = useQuery<{ phases: PhaseTagMapping[] }>({
    queryKey: ['deals-phase-meta'],
    queryFn: () => apiClient.get('/deals/meta/phases').then((r) => r.data),
    staleTime: 5 * 60_000,
  })

  const updateDeal = useMutation({
    mutationFn: ({ tanssTicketId, ...data }: { tanssTicketId: string } & Record<string, any>) =>
      apiClient.patch(`/deals/${tanssTicketId}`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      setEditingDeal(null)
    },
  })

  const moveDeal = useMutation({
    mutationFn: ({ tanssTicketId, fromTagId, toTagId }: { tanssTicketId: string; fromTagId: number; toTagId: number }) =>
      apiClient.post(`/deals/${tanssTicketId}/move`, { fromTagId, toTagId }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
    },
  })

  const moveProspect = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiClient.patch(`/acquisition/prospects/${id}`, { status }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vertrieb', 'prospects'] })
    },
  })

  // Group prospects + deals into 10 phase columns
  const pipeline: PipelinePhase[] = useMemo(() => {
    return PHASES.map((phase) => {
      const cards: BoardCard[] = []
      let totalValue = 0
      if (phase.source !== 'deal' && phase.prospectStatus) {
        for (const p of prospects) {
          if (p.status === phase.prospectStatus) cards.push({ kind: 'prospect', id: p.id, prospect: p })
        }
      }
      if (phase.source !== 'prospect' && phase.dealPhase) {
        for (const d of deals) {
          if (d.phase === phase.dealPhase) {
            cards.push({ kind: 'deal', id: d.id, deal: d })
            totalValue += d.value
          }
        }
      }
      // Sort: prospects by ICP score desc, deals by dueDate asc
      cards.sort((a, b) => {
        if (a.kind === 'prospect' && b.kind === 'prospect') return b.prospect.icpScore - a.prospect.icpScore
        if (a.kind === 'deal' && b.kind === 'deal') {
          const ad = a.deal.dueDate ? new Date(a.deal.dueDate).getTime() : Number.MAX_SAFE_INTEGER
          const bd = b.deal.dueDate ? new Date(b.deal.dueDate).getTime() : Number.MAX_SAFE_INTEGER
          return ad - bd
        }
        return a.kind === 'prospect' ? -1 : 1
      })
      return { ...phase, cards, totalValue }
    })
  }, [prospects, deals])

  const totalPipelineValue = deals.reduce((sum, d) => sum + d.value * (d.probability / 100), 0)
  const openDeals = deals.filter((d) => d.phase !== 'won' && d.phase !== 'completed')
  const openProspects = prospects.filter((p) => PROSPECT_ACTIVE_STATUSES.has(p.status))
  const wonDeals = deals.filter((d) => d.phase === 'won')
  const wonValue = wonDeals.reduce((sum, d) => sum + d.value, 0)
  const overdueCount = deals.filter((d) => d.dueDate && new Date(d.dueDate) < new Date()).length

  const isLoading = dealsLoading || prospectsLoading
  const tanssUnavailable = dealsError && (dealsError as any)?.response?.data?.code === 'TANSS_UNAVAILABLE'

  // Drag handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current as { kind?: 'prospect' | 'deal'; prospect?: Prospect; deal?: Deal } | undefined
    if (data?.kind === 'prospect' && data.prospect) {
      setActiveDrag({ kind: 'prospect', id: data.prospect.id, prospect: data.prospect })
    } else if (data?.kind === 'deal' && data.deal) {
      setActiveDrag({ kind: 'deal', id: data.deal.id, deal: data.deal })
    }
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveDrag(null)

    const { active, over } = event
    if (!over) return

    const data = active.data.current as { kind?: 'prospect' | 'deal'; prospect?: Prospect; deal?: Deal } | undefined
    if (!data?.kind) return

    const targetPhaseId = String(over.id)
    const targetPhase = PHASES.find((p) => p.id === targetPhaseId)
    if (!targetPhase) return

    // ── Prospect wird gezogen ────────────────────────────────────────────
    if (data.kind === 'prospect' && data.prospect) {
      const prospect = data.prospect
      const sourcePhase = phaseForProspect(prospect.status)
      if (sourcePhase?.id === targetPhaseId) return

      // Ziel akzeptiert Prospects
      if (targetPhase.source === 'prospect' || targetPhase.source === 'both') {
        const newStatus = targetPhase.prospectStatus
        if (!newStatus) return
        // Optimistic update
        queryClient.setQueryData<{ prospects: Prospect[]; total: number } | undefined>(
          ['vertrieb', 'prospects'],
          (old) => old ? { ...old, prospects: old.prospects.map((x) => x.id === prospect.id ? { ...x, status: newStatus } : x) } : old,
        )
        moveProspect.mutate({ id: prospect.id, status: newStatus })
        return
      }

      // Ziel ist Deal-Spalte mit Handoff-Einstieg (Lead / Angebot)
      if (targetPhase.source === 'deal' && targetPhase.handoffInitialTag) {
        // Handoff nur aus "Termin" sinnvoll, aus anderen Prospect-Phasen warnen
        if (prospect.status !== 'meeting_scheduled') {
          showDropError(`Handoff nur aus "Termin" möglich. Prospect aktuell in "${sourcePhase?.name ?? prospect.status}". Zuerst auf "Termin" ziehen.`)
          return
        }
        setHandoffProspect({
          prospect: {
            id: prospect.id,
            companyName: prospect.companyName,
            contactFirstName: prospect.contactFirstName,
            contactLastName: prospect.contactLastName,
            contactRole: prospect.contactRole,
            contactEmail: prospect.contactEmail,
            contactPhone: prospect.contactPhone,
            city: prospect.city,
            icpScore: prospect.icpScore,
          },
          initialPhase: targetPhase.handoffInitialTag,
        })
        return
      }

      showDropError('Zielspalte akzeptiert keinen Zielkunden ohne Handoff.')
      return
    }

    // ── Deal wird gezogen ────────────────────────────────────────────────
    if (data.kind === 'deal' && data.deal) {
      const deal = data.deal
      if (targetPhase.source === 'prospect') {
        showDropError('Deals können nicht zurück in die Zielkunden-Phasen verschoben werden.')
        return
      }
      const targetDealPhase = targetPhase.dealPhase
      if (!targetDealPhase || deal.phase === targetDealPhase) return
      if (!phaseMeta?.phases) return

      const targetPhaseTags = phaseMeta.phases.filter((p) => p.phase === targetDealPhase)
      const allPhaseTagIds = new Set(phaseMeta.phases.map((p) => p.tagId).filter(Boolean) as number[])
      const fromTagId = deal.tagIds?.find((id) => allPhaseTagIds.has(id))
        ?? phaseMeta.phases.filter((p) => p.phase === deal.phase).find((p) => p.tagId)?.tagId
      const toTagId = targetPhaseTags.find((p) => p.tagId)?.tagId

      if (!fromTagId || !toTagId) {
        showDropError('TANSS-Tag-IDs für diese Phase konnten nicht aufgelöst werden.')
        return
      }

      // Optimistic update
      queryClient.setQueryData<Deal[]>(['deals'], (old) =>
        old?.map((d) => d.id === deal.id ? { ...d, phase: targetDealPhase } : d),
      )
      moveDeal.mutate({ tanssTicketId: deal.tanssTicketId, fromTagId, toTagId })
    }
  }, [phaseMeta, moveDeal, moveProspect, queryClient, showDropError])

  const handleCardClick = useCallback((deal: Deal) => {
    setSelectedDeal(deal)
  }, [])

  const dealListColumns = useMemo<ColumnDef<Deal, unknown>[]>(
    () => [
      {
        id: 'ticket',
        header: 'Ticket',
        cell: ({ row }) => (
          <div>
            <div className="text-small font-medium text-gray-900">{row.original.title}</div>
            <div className="text-caption text-gray-400">#{a.docNum(row.original.tanssTicketId)}</div>
          </div>
        ),
      },
      {
        id: 'customer',
        header: 'Kunde',
        cell: ({ row }) => (
          <span className="text-small text-gray-700">
            {row.original.customerName ? a.name(row.original.customerName) : '—'}
          </span>
        ),
      },
      {
        id: 'phase',
        header: 'Phase',
        size: 140,
        cell: ({ row }) => {
          const phase = phaseForDeal(row.original.phase)
          return phase ? (
            <Badge variant={phase.badgeVariant} size="sm">{phase.name}</Badge>
          ) : (
            <span className="text-small text-gray-700">{row.original.phase}</span>
          )
        },
      },
      {
        accessorKey: 'value',
        header: 'Wert',
        size: 120,
        cell: ({ row }) => (
          <span className="text-small font-medium text-gray-900">
            {row.original.value > 0 ? a.fmtCurrency(row.original.value) : '—'}
          </span>
        ),
      },
      {
        accessorKey: 'probability',
        header: 'Wahrsch.',
        size: 90,
        cell: ({ row }) => (
          <span className="text-small text-gray-700">{row.original.probability}%</span>
        ),
      },
      textColumn<Deal>('assignedTo', 'Verantwortlich'),
      dateColumn<Deal>('dueDate', 'Fällig', { size: 120 }),
    ],
    [a],
  )

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-brand-500" />
            Vertriebsboard
            {isBeta && <BetaBadge size="md" />}
          </span>
        }
        description="Kompletter Funnel: Recherche bis Gewonnen"
        actions={
          <Tabs value={view} onValueChange={(v) => setView(v as 'board' | 'list')}>
            <TabsList>
              <TabsTrigger value="board">Kanban</TabsTrigger>
              <TabsTrigger value="list">Liste</TabsTrigger>
            </TabsList>
          </Tabs>
        }
      />

      {tanssUnavailable && (
        <div className="flex-shrink-0 mb-4">
          <ErrorState
            title="TANSS nicht verfügbar"
            description="Bitte Verbindung prüfen und Seite neu laden."
            size="sm"
          />
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 flex-shrink-0">
        <KpiCard icon={<UserPlus className="w-3.5 h-3.5" />} label="Offene Zielkunden" value={String(openProspects.length)} />
        <KpiCard icon={<DollarSign className="w-3.5 h-3.5" />} label="Pipeline-Wert (gewichtet)" value={a.fmtCurrency(totalPipelineValue)} />
        <KpiCard icon={<Users className="w-3.5 h-3.5" />} label="Offene Deals" value={String(openDeals.length)} />
        <KpiCard icon={<TrendingUp className="w-3.5 h-3.5" />} label="Gewonnen" value={a.fmtCurrency(wonValue)} valueClassName="text-green-600" />
        <KpiCard icon={<AlertTriangle className="w-3.5 h-3.5" />} label="Überfällig" value={String(overdueCount)} valueClassName={overdueCount > 0 ? 'text-red-600' : undefined} />
      </div>

      {isLoading && (
        <div className="flex-shrink-0">
          <LoadingState label="Lade Pipeline …" />
        </div>
      )}

      {/* Kanban Board – fills remaining vertical space */}
      {!isLoading && view === 'board' && (
        <DndContext
          sensors={sensors}
          collisionDetection={rectIntersection}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-3 mt-4 flex-1 min-h-0 pb-2 overflow-x-auto">
            {pipeline.map((phase) => (
              <DroppableColumn key={phase.id} phase={phase}>
                {phase.cards.map((card) =>
                  card.kind === 'deal' ? (
                    <DraggableDealCard
                      key={`deal:${card.id}`}
                      deal={card.deal}
                      onClick={() => handleCardClick(card.deal)}
                    />
                  ) : (
                    <DraggableProspectCard
                      key={`prospect:${card.id}`}
                      prospect={card.prospect}
                      onClick={() => { /* TODO: Prospect-Detail oeffnen */ }}
                    />
                  ),
                )}
              </DroppableColumn>
            ))}
          </div>

          <DragOverlay>
            {activeDrag?.kind === 'deal' ? (
              <DealCard deal={activeDrag.deal} onClick={() => {}} isOverlay />
            ) : activeDrag?.kind === 'prospect' ? (
              <ProspectCard prospect={activeDrag.prospect} onClick={() => {}} isOverlay />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* List View */}
      {!isLoading && view === 'list' && (
        <div className="mt-4 flex-1 min-h-0 overflow-y-auto">
          <DataTable<Deal>
            data={deals}
            columns={dealListColumns}
            getRowId={(d) => d.id}
            onRowClick={(d) => handleCardClick(d)}
            emptyState={
              <div className="py-12 text-center text-small text-gray-400">
                Keine TANSS-Tickets mit Vertrieb-Tags gefunden.
              </div>
            }
          />
        </div>
      )}

      {/* Ticket Detail Modal */}
      {selectedDeal && (
        <TicketDetailModal deal={selectedDeal} onClose={() => setSelectedDeal(null)} />
      )}

      {/* Edit Deal Metadata */}
      {editingDeal && (
        <DealMetadataDialog
          deal={editingDeal}
          onSave={(data) => updateDeal.mutate({ tanssTicketId: editingDeal.tanssTicketId, ...data })}
          onClose={() => setEditingDeal(null)}
          saving={updateDeal.isPending}
        />
      )}

      {/* Handoff Dialog */}
      {handoffProspect && (
        <HandoffDialog
          prospect={handoffProspect.prospect}
          initialPhase={handoffProspect.initialPhase}
          onClose={() => setHandoffProspect(null)}
          onSuccess={() => {
            setHandoffProspect(null)
          }}
        />
      )}
    </div>
  )
}
